import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { AuthInlineWidget } from './AuthInlineWidget';

type GetStartedAuthModalProps = {
  open: boolean;
  onClose: () => void;
  onSignedIn: () => void;
};

/**
 * “Get started” entry: tabbed sign-in and sign-up on the home page.
 */
export function GetStartedAuthModal({ open, onClose, onSignedIn }: GetStartedAuthModalProps) {
  const [tab, setTab] = useState<'login' | 'signup'>('login');

  useEffect(() => {
    if (open) {
      setTab('login');
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-modal-content flex items-end justify-center p-0 sm:items-center sm:p-4" role="presentation">
      <button
        type="button"
        className="absolute inset-0 z-0 bg-base/80 backdrop-blur-sm"
        aria-label="Close"
        onClick={onClose}
      />
      <div
        className="relative z-10 flex max-h-[min(100dvh,720px)] w-full max-w-md flex-col overflow-y-auto p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:p-0"
        role="dialog"
        aria-modal="true"
        aria-labelledby="get-started-auth-title"
      >
        <h2 id="get-started-auth-title" className="sr-only">
          Account
        </h2>
        <div className="mb-3 flex items-center justify-between gap-2 sm:mb-4">
          <div className="flex flex-1 rounded-lg bg-overlay p-1" role="tablist" aria-labelledby="get-started-auth-title">
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'login'}
              className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition ${
                tab === 'login' ? 'bg-elevated text-th-primary shadow-sm' : 'text-th-secondary hover:text-th-primary'
              }`}
              onClick={() => setTab('login')}
            >
              Sign in
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'signup'}
              className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition ${
                tab === 'signup' ? 'bg-elevated text-th-primary shadow-sm' : 'text-th-secondary hover:text-th-primary'
              }`}
              onClick={() => setTab('signup')}
            >
              Get started
            </button>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-lg p-2 text-th-secondary transition hover:bg-overlay hover:text-th-primary"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <AuthInlineWidget
          key={tab}
          layout="modal"
          defaultView={tab}
          hideAuthSwitchLinks
          hideHeader
          onLoginSuccess={onSignedIn}
          onSignupSuccess={onSignedIn}
        />
      </div>
    </div>
  );
}
